/**
 * Resolving what price columns a Promo Office quote line should show.
 *
 * The design called for a quantity price-break ladder with sell and buy side
 * by side, so margin could be judged at any order size. Live data does not
 * support it: `SellingRelays`/`BuyingRelays` are empty for most products, no
 * other endpoint carries tiered pricing, and the two that might were checked
 * and return errors. See the 2026-09-11 amendment in
 * `docs/features/promo-office-visibility/01_product_brief.md`.
 *
 * What is always real is the price committed on the line itself — the
 * quantity quoted and its unit prices. So a line shows its ladder when one
 * exists, and otherwise the single row it was actually quoted at. Never a
 * fabricated tier, and never an empty table.
 */

import type {
  PromoOfficeLineItem,
  PromoOfficePriceBreak,
} from "../data/adapter";

export interface QuotePriceColumn {
  quantity: number;
  sellUnitPrice: number;
  costUnitPrice: number;
  /**
   * True when this column is the quantity the line was actually quoted at,
   * rather than a rung of a published ladder. The UI uses it to label the
   * column honestly instead of implying a tier that does not exist.
   */
  isQuotedQuantity: boolean;
}

/**
 * The unit price applying at `quantity`, given a ladder.
 *
 * Rungs state a *minimum* quantity, so the applicable price is the highest
 * rung at or below the quantity asked for. Returns 0 when the ladder has no
 * rung that low — which reads as "not priced at this quantity", not "free".
 */
export function unitPriceAt(
  breaks: PromoOfficePriceBreak[],
  quantity: number,
): number {
  const applicable = breaks
    .filter((b) => b.quantity <= quantity)
    .sort((a, b) => b.quantity - a.quantity)[0];
  return applicable?.unitPrice ?? 0;
}

/**
 * The columns a line's price table should render.
 *
 * One per ladder rung when a ladder exists; otherwise a single column for the
 * quantity actually quoted. Always at least one column, so the table never
 * renders as bare row labels with nothing beside them.
 */
export function quotePriceColumns(
  line: PromoOfficeLineItem,
): QuotePriceColumn[] {
  if (line.sellPriceBreaks.length > 0) {
    return line.sellPriceBreaks.map((b) => ({
      quantity: b.quantity,
      sellUnitPrice: b.unitPrice,
      costUnitPrice: unitPriceAt(line.costPriceBreaks, b.quantity),
      isQuotedQuantity: false,
    }));
  }
  return [
    {
      quantity: line.quantity,
      sellUnitPrice: line.sellUnitPrice,
      costUnitPrice: line.costUnitPrice,
      isQuotedQuantity: true,
    },
  ];
}

/** True when no ladder was published and the table shows the quoted row only. */
export function hasPublishedLadder(line: PromoOfficeLineItem): boolean {
  return line.sellPriceBreaks.length > 0;
}
